// src/app/store.ts
import { configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { rootReducer } from './rootReducer';

// P2-8: NEVER persist auth tokens to localStorage. The access token lives in an
// httpOnly cookie (XSS-unreadable); the session is restored on load via the
// gateway /session endpoint. Strip `tokens` from the auth slice on the way OUT to
// storage so an XSS can't lift a reusable credential from redux-persist.
const stripAuthTokens = createTransform(
  (inboundState: unknown, key) => {
    if (key === 'auth' && inboundState && typeof inboundState === 'object') {
      return { ...(inboundState as Record<string, unknown>), tokens: null };
    }
    return inboundState;
  },
  (outboundState) => outboundState,
  { whitelist: ['auth'] },
);

// Persist configuration
const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['auth', 'tenant'], // Only persist auth and tenant state
  blacklist: ['ui'], // Don't persist UI state
  transforms: [stripAuthTokens],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        // Ignore these paths in the state
        ignoredPaths: ['auth.tokenRefreshTimer'],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

// Create persistor
export const persistor = persistStore(store);

// Infer types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Export store methods for external use
export const { dispatch, getState, subscribe } = store;